import React, { useState, useEffect } from 'react';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  AutoAwesome as AutoGeneratedIcon
} from '@mui/icons-material';
import { useNotifications } from '../../../hooks/useNotifications';
import { useDataLayerValue } from '../../../contextAPI/DataLayer';
import { useLoading } from '../../../hooks/useLoading';
import {
  fetchChecklistRepository,
  submitChecklistRepository,
  downloadChecklistRepository,
  deleteChecklistRepository,
  getChecklistRepositoryById,
  downloadChecklistVersion
} from '../../../constants/requests';
import './checklistRepository.css';

const ChecklistRepository = () => {
  const [{ _id }] = useDataLayerValue();
  const { showNotifications } = useNotifications();
  const { request } = useLoading();

  const [checklists, setChecklists] = useState([]);
  const [filteredChecklists, setFilteredChecklists] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAutoGeneratedOnly, setShowAutoGeneratedOnly] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'upload'
  const [selectedChecklistId, setSelectedChecklistId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [checklistToDelete, setChecklistToDelete] = useState(null);
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [selectedChecklistVersions, setSelectedChecklistVersions] =
    useState(null);

  const [formData, setFormData] = useState({
    documentName: '',
    tags: '',
    file: null
  });

  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    fetchChecklists();
  }, []);

  useEffect(() => {
    let filtered = checklists;

    if (showAutoGeneratedOnly) {
      filtered = filtered.filter((checklist) => isAutoGenerated(checklist));
    }

    if (searchQuery.trim()) {
      filtered = filtered.filter((checklist) => {
        const nameMatch = checklist.documentName
          .toLowerCase()
          .includes(searchQuery.toLowerCase());
        const tagMatch =
          checklist.tags &&
          checklist.tags.some((tag) =>
            tag.toLowerCase().includes(searchQuery.toLowerCase())
          );
        return nameMatch || tagMatch;
      });
    }

    setFilteredChecklists(filtered);
  }, [searchQuery, checklists, showAutoGeneratedOnly]);

  const fetchChecklists = async () => {
    setIsLoading(true);
    try {
      const response = await request(() => fetchChecklistRepository(_id));

      if (response && response.data && response.data.body) {
        const checklistData = response.data.body || [];

        const transformedData = checklistData.map((checklist) => ({
          _id: checklist._id,
          documentName: checklist.documentName,
          tags: checklist.tags || [],
          uploadedAt: checklist.createdAt,
          uploadedBy: checklist.uploadedByName || 'Unknown User',
          fileSize:
            checklist.formattedFileSize || formatFileSize(checklist.fileSize),
          version: checklist.version,
          fileType: checklist.fileType
        }));

        setChecklists(transformedData);
        setFilteredChecklists(transformedData);
      } else {
        throw new Error('Failed to fetch checklists');
      }
    } catch (error) {
      showNotifications('error', 'Failed to load checklists', 5000);
      setChecklists([]);
      setFilteredChecklists([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (mode, checklistId = null) => {
    setModalMode(mode);
    setSelectedChecklistId(checklistId);
    setIsModalOpen(true);
    setFormData({
      documentName: '',
      tags: '',
      file: null
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormData({
      documentName: '',
      tags: '',
      file: null
    });
    setSelectedChecklistId(null);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword'
      ];

      if (!allowedTypes.includes(file.type)) {
        showNotifications(
          'error',
          'Please upload a PDF, Excel, or Word document',
          5000
        );
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        showNotifications('error', 'File size should be less than 10MB', 5000);
        return;
      }

      setFormData((prev) => ({
        ...prev,
        file: file
      }));
      showNotifications('success', 'File selected successfully', 3000);
    }
  };

  const handleSubmit = async () => {
    if (!formData.documentName.trim()) {
      showNotifications('error', 'Document name is required', 3000);
      return;
    }

    if (!formData.file) {
      showNotifications('error', 'Please select a file to upload', 3000);
      return;
    }

    setIsUploading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('file', formData.file);
      formDataToSend.append('documentName', formData.documentName);
      formDataToSend.append('tags', formData.tags);

      if (modalMode === 'upload' && selectedChecklistId) {
        formDataToSend.append('checklistId', selectedChecklistId);
      }

      const isUpload = modalMode === 'upload';
      const response = await request(() =>
        submitChecklistRepository(
          formDataToSend,
          _id,
          isUpload,
          selectedChecklistId
        )
      );

      if (response && response.data) {
        showNotifications(
          'success',
          modalMode === 'add'
            ? 'Checklist added successfully!'
            : 'New version uploaded successfully!',
          5000
        );
        handleCloseModal();
        fetchChecklists();
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showNotifications(
        'error',
        'Failed to upload checklist. Please try again.',
        5000
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async (checklistId, documentName) => {
    try {
      const response = await request(() =>
        downloadChecklistRepository(checklistId, _id)
      );

      if (response && response.data) {
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = documentName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        showNotifications('success', 'Download started', 3000);
      } else {
        throw new Error('Download failed');
      }
    } catch (error) {
      console.error('Download error:', error);
      showNotifications('error', 'Failed to download checklist', 5000);
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatFileSize = (size) => {
    if (typeof size === 'string') return size;
    const units = ['B', 'KB', 'MB', 'GB'];
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const isAutoGenerated = (checklist) => {
    return checklist.tags && checklist.tags.includes('auto-generated');
  };

  const handleDeleteClick = (checklistId, documentName) => {
    setChecklistToDelete({ id: checklistId, name: documentName });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!checklistToDelete) return;

    try {
      const response = await request(() =>
        deleteChecklistRepository(checklistToDelete.id, _id)
      );

      if (response && response.data) {
        showNotifications(
          'success',
          response.data.message || 'Checklist deleted successfully',
          3000
        );
        fetchChecklists(); // Refresh the list
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      showNotifications(
        'error',
        error.message || 'Failed to delete checklist',
        5000
      );
    } finally {
      setDeleteDialogOpen(false);
      setChecklistToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setChecklistToDelete(null);
  };

  const handleViewVersions = async (checklist) => {
    try {
      const response = await request(() =>
        getChecklistRepositoryById(checklist._id, _id)
      );

      if (response && response.data && response.data.body) {
        const checklistData = response.data.body;

        const versions = checklistData.documentManagerId?.versions || [];
        setSelectedChecklistVersions({
          ...checklist,
          versions: versions.map((version, index) => ({
            ...version,
            versionNumber: versions.length - index, // Latest version gets highest number
            uploadedAt: version.createdAt,
            isLatest: index === versions.length - 1
          }))
        });
        setVersionDialogOpen(true);
      } else {
        throw new Error('Failed to fetch versions');
      }
    } catch (error) {
      console.error('Error fetching versions:', error);
      showNotifications('error', 'Failed to load versions', 5000);
    }
  };

  const handleDownloadVersion = async (
    versionId,
    documentName,
    versionNumber
  ) => {
    try {
      const response = await request(() =>
        downloadChecklistVersion(selectedChecklistVersions._id, versionId, _id)
      );

      if (
        response &&
        response.data &&
        response.data.body &&
        response.data.body.downloadUrl
      ) {
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = response.data.body.downloadUrl;
        a.download = `${documentName}_v${versionNumber}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        showNotifications(
          'success',
          `Version ${versionNumber} download started`,
          3000
        );
      } else {
        throw new Error('Download URL not available');
      }
    } catch (error) {
      console.error('Download error:', error);
      showNotifications(
        'error',
        error.message || 'Failed to download version',
        5000
      );
    }
  };

  return (
    <div>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem'
        }}
      >
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Checklist Repository
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenModal('add')}
          sx={{
            backgroundColor: 'black',
            '&:hover': {
              backgroundColor: '#333'
            }
          }}
        >
          Add Checklist
        </Button>
      </Box>

      <Typography variant="body1" sx={{ marginBottom: '2rem', color: '#666' }}>
        Manage your checklist documents repository. Upload, download, and
        maintain versions of your compliance checklists.
      </Typography>

      <Box sx={{ marginBottom: '2rem' }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by document name or tags..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#666' }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleClearSearch}
                  sx={{ color: '#666' }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{
            maxWidth: '500px',
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#f8f9fa'
            }
          }}
        />
        {searchQuery && (
          <Typography
            variant="body2"
            sx={{ marginTop: '0.5rem', color: '#666' }}
          >
            {filteredChecklists.length} result
            {filteredChecklists.length !== 1 ? 's' : ''} found
          </Typography>
        )}

        <Box
          sx={{
            marginTop: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <Chip
            icon={<AutoGeneratedIcon />}
            label="Show Auto-Generated Only"
            onClick={() => setShowAutoGeneratedOnly(!showAutoGeneratedOnly)}
            color={showAutoGeneratedOnly ? 'primary' : 'default'}
            variant={showAutoGeneratedOnly ? 'filled' : 'outlined'}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: showAutoGeneratedOnly
                  ? 'primary.dark'
                  : 'action.hover'
              }
            }}
          />
          {showAutoGeneratedOnly && (
            <Typography variant="body2" sx={{ color: '#666' }}>
              Showing {filteredChecklists.length} auto-generated checklist
              {filteredChecklists.length !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>
      </Box>

      {isLoading ? (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
          <Table sx={{ minWidth: 650 }} aria-label="checklist repository table">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Document Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Tags</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Version</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>File Size</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Uploaded By</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Uploaded At</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredChecklists.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    sx={{
                      textAlign: 'center',
                      padding: '2rem',
                      color: '#666'
                    }}
                  >
                    {searchQuery
                      ? `No checklists found matching "${searchQuery}". Try a different search term.`
                      : 'No checklists found. Click Add Checklist to get started.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredChecklists.map((checklist) => (
                  <TableRow
                    key={checklist._id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 'medium' }}
                        >
                          {checklist.documentName}
                        </Typography>
                        {isAutoGenerated(checklist) && (
                          <Tooltip title="Auto-generated from ChecklistGenerator">
                            <AutoGeneratedIcon
                              fontSize="small"
                              sx={{
                                color: '#9c27b0',
                                animation: 'pulse 2s infinite'
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: '0.5rem',
                          flexWrap: 'wrap'
                        }}
                      >
                        {checklist.tags && checklist.tags.length > 0 ? (
                          checklist.tags.map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" sx={{ color: '#999' }}>
                            No tags
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {checklist.version || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatFileSize(checklist.fileSize)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {checklist.uploadedBy || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(checklist.uploadedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: '0.5rem' }}>
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDownload(
                                checklist._id,
                                checklist.documentName
                              )
                            }
                            sx={{ color: '#1976d2' }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Upload New Version">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleOpenModal('upload', checklist._id)
                            }
                            sx={{ color: '#2e7d32' }}
                          >
                            <UploadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View All Versions">
                          <IconButton
                            size="small"
                            onClick={() => handleViewVersions(checklist)}
                            sx={{ color: '#ed6c02' }}
                          >
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDeleteClick(
                                checklist._id,
                                checklist.documentName
                              )
                            }
                            sx={{ color: '#d32f2f' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {modalMode === 'add' ? 'Add New Checklist' : 'Upload New Version'}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              paddingTop: '1rem'
            }}
          >
            <TextField
              fullWidth
              label="Document Name"
              value={formData.documentName}
              onChange={(e) =>
                handleInputChange('documentName', e.target.value)
              }
              required
            />

            <TextField
              fullWidth
              label="Tags (comma-separated)"
              value={formData.tags}
              onChange={(e) => handleInputChange('tags', e.target.value)}
              placeholder="e.g., compliance, audit, regulation"
              helperText="Enter tags separated by commas"
            />

            <Box>
              <input
                type="file"
                id="checklist-upload"
                accept=".pdf,.xlsx,.xls,.docx,.doc"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <label htmlFor="checklist-upload">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  sx={{
                    padding: '1rem',
                    borderStyle: 'dashed',
                    borderWidth: '2px',
                    '&:hover': {
                      borderStyle: 'dashed',
                      borderWidth: '2px'
                    }
                  }}
                >
                  {formData.file
                    ? formData.file.name
                    : 'Choose File (PDF, Excel, Word)'}
                </Button>
              </label>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={isUploading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isUploading}
            sx={{
              backgroundColor: 'black',
              '&:hover': {
                backgroundColor: '#333'
              }
            }}
          >
            {isUploading ? (
              <>
                <CircularProgress size={20} sx={{ marginRight: '0.5rem' }} />
                {modalMode === 'add' ? 'Adding...' : 'Uploading...'}
              </>
            ) : modalMode === 'add' ? (
              'Add Checklist'
            ) : (
              'Upload Version'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description">
            Are you sure you want to delete {checklistToDelete?.name}? This
            action cannot be undone and will permanently remove the checklist
            from the repository.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={versionDialogOpen}
        onClose={() => setVersionDialogOpen(false)}
        maxWidth="md"
        fullWidth
        aria-labelledby="version-dialog-title"
      >
        <DialogTitle id="version-dialog-title">
          Version History - {selectedChecklistVersions?.documentName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ marginTop: '1rem' }}>
            {selectedChecklistVersions?.versions?.length > 0 ? (
              <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Version</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        Uploaded At
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedChecklistVersions.versions.map((version) => (
                      <TableRow key={version.versionId}>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: version.isLatest ? 'bold' : 'normal'
                            }}
                          >
                            v{version.versionNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(version.uploadedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {version.isLatest && (
                            <Chip
                              label="Latest"
                              color="primary"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Download this version">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDownloadVersion(
                                  version.versionId,
                                  selectedChecklistVersions.documentName,
                                  version.versionNumber
                                )
                              }
                              sx={{ color: '#1976d2' }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography
                variant="body2"
                sx={{ textAlign: 'center', padding: '2rem', color: '#666' }}
              >
                No versions found for this checklist.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ChecklistRepository;
